import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { CalendarPlus, Clock, Home, Star, User, type LucideIcon } from 'lucide-react';
import { cx, useTenant } from '@barber/ui';

/**
 * A casca do app.
 *
 * ---
 *
 * NAVEGAÇÃO EMBAIXO, E NÃO EM CIMA
 *
 * O app é usado com uma mão, no celular. O topo da tela de um aparelho grande
 * não é alcançável com o polegar; a base é. Menu lateral escondido atrás de um
 * ícone custaria um toque a mais em cada troca de seção — e a seção mais usada
 * ("meus horários") é justamente uma consulta rápida.
 */

const ITENS: readonly { to: string; label: string; icon: LucideIcon; exact?: boolean }[] = [
  { to: '/', label: 'Início', icon: Home, exact: true },
  { to: '/agendar', label: 'Agendar', icon: CalendarPlus },
  { to: '/horarios', label: 'Horários', icon: Clock },
  { to: '/plano', label: 'Plano', icon: Star },
  { to: '/perfil', label: 'Perfil', icon: User },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { tenant } = useTenant();
  const location = useLocation();

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-lg flex-col bg-surface-muted">
      <header className="sticky top-0 z-10 border-b border-border bg-surface/95 backdrop-blur">
        <div className="flex items-center gap-3 px-4 py-3">
          {tenant.branding.logoUrl ? (
            <img
              src={tenant.branding.logoUrl}
              alt={tenant.name}
              className="h-9 w-9 rounded-lg object-cover"
            />
          ) : (
            <span
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand text-sm font-bold text-brand-fg"
              aria-hidden="true"
            >
              {tenant.name.slice(0, 2).toUpperCase()}
            </span>
          )}

          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-content">{tenant.name}</p>
            {tenant.address.city && (
              <p className="truncate text-xs text-content-muted">
                {tenant.address.district ? `${tenant.address.district} · ` : ''}
                {tenant.address.city}
              </p>
            )}
          </div>
        </div>
      </header>

      {/*
        `key` na rota: cada troca de tela recomeça a rolagem do zero. Sem isso,
        entrar numa lista longa e voltar deixa a próxima tela no meio.
      */}
      <main key={location.pathname} className="flex-1 px-4 pb-24 pt-4">
        {children}
      </main>

      <nav
        aria-label="Navegação principal"
        className="fixed inset-x-0 bottom-0 z-20 mx-auto w-full max-w-lg border-t border-border bg-surface pb-[env(safe-area-inset-bottom)]"
      >
        <ul className="flex">
          {ITENS.map((item) => (
            <li key={item.to} className="flex-1">
              <NavLink
                to={item.to}
                end={'exact' in item ? item.exact : false}
                className={({ isActive }) =>
                  cx(
                    'tap-target flex flex-col items-center gap-0.5 py-2 text-[11px] font-medium transition',
                    isActive ? 'text-brand' : 'text-content-muted',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon
                      className="h-5 w-5"
                      aria-hidden="true"
                      strokeWidth={isActive ? 2.4 : 1.8}
                    />
                    <span>{item.label}</span>
                    {/* O estado atual precisa ser anunciado, não só colorido. */}
                    {isActive && <span className="sr-only">(seção atual)</span>}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

/** Cabeçalho de tela, com o título que também vira o do documento. */
export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-5 flex items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-content">{title}</h1>
        {description && <p className="mt-1 text-sm text-content-muted">{description}</p>}
      </div>
      {action}
    </div>
  );
}
